/**
 * NFC Solutions — Thermal receipt printer helper.
 *
 * Session 27.4. Purpose: reliably print an 80 mm thermal receipt to
 * the SNBC BTP-R880NPU1 (or any 80 mm ESC/POS-class receipt printer)
 * from a browser page, without the receipt content overflowing to a
 * second page and without dragging the surrounding Tailwind styles
 * into the print output.
 *
 * ─────────────────────────────────────────────────────────────────
 *  Why not just call `window.print()` on the visible receipt div?
 * ─────────────────────────────────────────────────────────────────
 *  The visible receipt is rendered inside a Radix Dialog, which lives
 *  inside a fixed-position overlay, which lives inside the main app
 *  shell.  When you invoke window.print() the browser hands the ENTIRE
 *  document to the print pipeline.  Even with `visibility:hidden` on
 *  everything except `#receipt-print-area`, page height and page break
 *  points are still computed against the full body layout — that's
 *  what was causing the "one blank sheet, then the receipt cut off on
 *  the second sheet" bug.  Windows/Edge respected `@page size: 80mm`
 *  for the drawable area but kept the outer document at whatever the
 *  driver reported (often ~297mm), which left an empty first page.
 *
 *  Fix: build a *self-contained* HTML document (business header, items,
 *  totals, footer) in a hidden `<iframe>`, style it with a tiny inline
 *  stylesheet keyed to the physical 80 mm strip, and call print() on
 *  the iframe's own contentWindow.  The main document isn't involved,
 *  so page layout collapses to exactly the receipt content and nothing
 *  else.
 */

const IFRAME_ID = "nfcs-thermal-print-frame";
const CURRENCY = "R"; // formatPrice already returns a leading R for ZAR

function _esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function _money(n, fmt) {
  const v = Number(n) || 0;
  if (typeof fmt === "function") return fmt(v);
  return `${CURRENCY}${v.toFixed(2)}`;
}

/**
 * Build the pure HTML body of the receipt from a normalised data
 * object. Kept as a pure function so the app can also render a
 * preview or run unit tests without touching the DOM.
 */
export function buildReceiptHtml(data) {
  const {
    business = {},
    tableLabel = "",
    orderLabel = "",     // e.g. "Order HB-1042" (Session 30.7)
    servedBy = "",
    header = "",
    dateStr = new Date().toLocaleString(),
    lines = [],           // [{ qty, name, price, modifiers?: string[], voided?: boolean }]
    subtotal = 0,
    vatRate = null,       // number OR null (if null, no VAT line printed)
    vatAmount = null,     // computed VAT (or auto-derive from subtotal when null)
    voucherDiscount = 0,
    voucherLabel = "",
    serviceFeePct = 0,    // Session 34 — automatic service fee (gratuity)
    serviceFeeAmount = 0,
    total = 0,
    payments = [],        // [{ label, amount }]
    tip = 0,
    gratuityRecipient = "", // Session 35 — waiter the service fee + tip go to
    changeDue = 0,
    footer = "Thank you!",
    poweredBy = "Powered by NFC Solutions",
    formatPrice = null,
  } = data || {};

  const money = (n) => _money(n, formatPrice);

  const linesHtml = lines.map((ln) => {
    // Support a "heading" row shape: an entry with empty qty / price is
    // rendered as a plain centred subheading (used for the "— Order
    // SS-1234 —" separator between orders on multi-order tables).
    const isHeading = (ln.qty === "" || ln.qty == null) && (ln.price === "" || ln.price == null);
    if (isHeading) {
      return `<div class="order-heading">${_esc(ln.name || "")}</div>`;
    }
    const qty = ln.qty || 1;
    const name = _esc(ln.name || "");
    const price = money(ln.price || 0);
    const mods = Array.isArray(ln.modifiers) ? ln.modifiers.filter(Boolean) : [];
    const voided = ln.voided ? " void" : "";
    return `
      <div class="row${voided}">
        <span class="qty">${qty}×</span>
        <span class="name">${name}</span>
        <span class="price">${_esc(price)}</span>
      </div>
      ${mods.map((m) => `<div class="mod">· ${_esc(m)}</div>`).join("")}
    `;
  }).join("");

  const vatLine = (vatRate != null && Number(vatRate) > 0) ? `
    <div class="row">
      <span class="label">VAT (${_esc(String(vatRate))}%)</span>
      <span class="price">${_esc(money(vatAmount != null ? vatAmount : (subtotal - subtotal / (1 + Number(vatRate) / 100))))}</span>
    </div>` : "";

  const voucherLine = voucherDiscount > 0 ? `
    <div class="row">
      <span class="label">Voucher ${_esc(voucherLabel || "")}</span>
      <span class="price">-${_esc(money(voucherDiscount))}</span>
    </div>` : "";

  const serviceFeeLine = Number(serviceFeeAmount) > 0 ? `
    <div class="row">
      <span class="label">Service fee (${_esc(String(serviceFeePct))}%)</span>
      <span class="price">${_esc(money(serviceFeeAmount))}</span>
    </div>` : "";

  const paymentsHtml = payments.filter((p) => Number(p.amount) > 0).map((p) => `
    <div class="row">
      <span class="label">${_esc(p.label || "")}</span>
      <span class="price">${_esc(money(p.amount || 0))}</span>
    </div>
  `).join("");

  const tipLine = tip > 0 ? `
    <div class="row">
      <span class="label">${Number(serviceFeeAmount) > 0 ? "Extra tip" : "Tip"}</span>
      <span class="price">${_esc(money(tip))}</span>
    </div>` : "";

  // Session 35 — say WHO the gratuity goes to (the waiter who opened the
  // table). Printed whenever there is a service fee and/or a tip.
  const gratuityTotal = Math.round((Number(serviceFeeAmount || 0) + Number(tip || 0)) * 100) / 100;
  const gratuityLabel = Number(serviceFeeAmount) > 0 && tip > 0
    ? "Service fee + tip for"
    : Number(serviceFeeAmount) > 0 ? "Service fee for" : "Tip for";
  const gratuityLine = gratuityRecipient && gratuityTotal > 0 ? `
    <div class="row gratuity">
      <span class="label">${_esc(gratuityLabel)} ${_esc(gratuityRecipient)}</span>
      <span class="price">${_esc(money(gratuityTotal))}</span>
    </div>` : "";

  const changeLine = changeDue > 0 ? `
    <div class="row">
      <span class="label">Change</span>
      <span class="price">${_esc(money(changeDue))}</span>
    </div>` : "";

  return `
<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Receipt</title>
<style>
  /* Session 27.4d — thermal print stylesheet, density-boosted.
     Operators reported the previous 11px/normal-weight text looked
     bland/faded on the SNBC BTP-R880NPU1 thermal head. Thermal
     printers darken proportionally to stroke width, so we bump both
     font-size and weight — the paper is only used for reading a
     receipt at arm's length, not for compact laser output. */
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    background: #fff; color: #000;
    /* Monospaced keeps price columns aligned like a classic POS slip. */
    font-family: 'Source Code Pro', 'Courier New', Courier, monospace;
    /* Session 30.13 — DARK PRINT: heavier weight + larger size so the
       thermal head burns wider, darker strokes everywhere. */
    font-size: 14px;
    font-weight: 800;
    line-height: 1.4;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    -webkit-font-smoothing: none;
    text-shadow: 0 0 0.2px #000;
  }
  body {
    width: 72mm;
    padding: 2mm 4mm 6mm 4mm;
  }
  .center { text-align: center; }
  .biz {
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.5px;
    margin-bottom: 1.5mm;
  }
  .header-meta {
    font-size: 12px;
    font-weight: 800;
    margin: 0;
  }
  .divider {
    border: 0;
    border-top: 2px dashed #000;
    margin: 2.5mm 0;
    height: 0;
  }
  .row {
    display: flex; justify-content: space-between; gap: 4px;
    page-break-inside: avoid;
    margin: 0.6mm 0;
  }
  .row .qty { flex: 0 0 auto; font-weight: 800; }
  .row .name { flex: 1 1 auto; padding-left: 4px; word-break: break-word; }
  .row .label { flex: 1 1 auto; font-weight: 700; }
  .row .price { flex: 0 0 auto; font-weight: 700; font-variant-numeric: tabular-nums; }
  .row.void { text-decoration: line-through; opacity: 0.9; }
  .mod {
    padding-left: 14px;
    font-size: 11px;
    font-weight: 700;
    color: #000;
    page-break-inside: avoid;
  }
  .total-row {
    font-size: 16px;
    font-weight: 900;
    border-top: 2px solid #000;
    padding-top: 2mm;
    margin-top: 2mm;
  }
  .gratuity {
    border-top: 1px dotted #000;
    padding-top: 1.5mm;
    margin-top: 1.5mm;
    font-weight: 800;
  }
  .total-row .label,
  .total-row .price {
    font-weight: 900;
  }
  .footer {
    text-align: center;
    margin-top: 4mm;
    font-size: 13px;
    font-weight: 700;
  }
  .powered {
    text-align: center;
    margin-top: 3mm;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.3px;
    color: #000;
  }
  .table-label { font-size: 15px; font-weight: 800; margin: 1mm 0; }
  .order-heading {
    text-align: center;
    font-weight: 800;
    font-size: 13px;
    margin: 2mm 0 1mm 0;
    padding: 1mm 0;
    border-top: 1px dotted #000;
    border-bottom: 1px dotted #000;
    page-break-inside: avoid;
  }
</style>
</head>
<body>
  <div class="center">
    <div class="biz">${_esc(business.name || "")}</div>
    ${business.address ? `<p class="header-meta">${_esc(business.address)}</p>` : ""}
    ${business.phone   ? `<p class="header-meta">${_esc(business.phone)}</p>`   : ""}
    ${business.vat_number ? `<p class="header-meta">VAT: ${_esc(business.vat_number)}</p>` : ""}
    ${tableLabel ? `<p class="header-meta table-label">${_esc(tableLabel)}</p>` : ""}
    ${orderLabel ? `<p class="header-meta"><strong>${_esc(orderLabel)}</strong></p>` : ""}
    ${header ? `<p class="header-meta">${_esc(header)}</p>` : ""}
    <p class="header-meta">${_esc(dateStr)}</p>
    ${servedBy ? `<p class="header-meta">Served by ${_esc(servedBy)}</p>` : ""}
  </div>
  <hr class="divider">
  ${linesHtml || `<div class="center">(no items)</div>`}
  <hr class="divider">
  <div class="row">
    <span class="label">Subtotal</span>
    <span class="price">${_esc(money(subtotal))}</span>
  </div>
  ${vatLine}
  ${voucherLine}
  ${serviceFeeLine}
  <div class="row total-row">
    <span class="label">TOTAL</span>
    <span class="price">${_esc(money(total))}</span>
  </div>
  ${paymentsHtml ? `<hr class="divider">${paymentsHtml}` : ""}
  ${tipLine}
  ${changeLine}
  ${gratuityLine}
  <div class="footer">${_esc(footer)}</div>
  ${poweredBy ? `<div class="powered">${_esc(poweredBy)}</div>` : ""}
</body>
</html>`;
}

/**
 * Print a receipt via a hidden iframe. Returns a Promise that resolves
 * as soon as the print dialog has been dispatched. The iframe is
 * removed after ~2 seconds so subsequent prints get a clean frame.
 *
 * Notes for Edge on Windows (per the SNBC BTP-R880NPU1 setup):
 *   - The browser still shows the print dialog with the current
 *     printer preselected. Pressing Enter (or Ctrl+P then Enter) fires
 *     one print job. This is not bypassable in a normal browser.
 *   - To skip the dialog entirely, run Edge/Chrome with the
 *     `--kiosk-printing` flag AND set the SNBC as the OS default
 *     printer. See docs/PRINTING.md.
 */
export function printReceipt(data) {
  const html = buildReceiptHtml(data || {});
  // Reuse the same hidden iframe on repeated prints — creating a new
  // one every time can flash the layout on some browsers.
  let iframe = document.getElementById(IFRAME_ID);
  if (!iframe) {
    iframe = document.createElement("iframe");
    iframe.id = IFRAME_ID;
    // Position off-screen but not display:none — some browsers won't
    // print content of a display:none iframe.
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);
  }
  return new Promise((resolve) => {
    const done = () => {
      // Delay removal so the print job has time to be dispatched.
      setTimeout(() => {
        try { iframe.parentNode && iframe.parentNode.removeChild(iframe); }
        catch (_) { /* noop */ }
      }, 2000);
      resolve(true);
    };
    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      // Wait for the iframe to fully layout before printing so all
      // widths/heights are computed.
      const cw = iframe.contentWindow;
      const fire = () => {
        try { cw.focus(); cw.print(); }
        catch (e) { /* noop */ }
        finally { done(); }
      };
      // requestAnimationFrame guarantees the browser has painted at
      // least one frame with the receipt content in place.
      if (cw && cw.requestAnimationFrame) {
        cw.requestAnimationFrame(() => cw.requestAnimationFrame(fire));
      } else {
        setTimeout(fire, 100);
      }
    } catch (err) {
      // If the iframe route fails for any reason, fall back to the
      // legacy window.print — the user still gets a print dialog with
      // the old (imperfect) rendering, which is better than a silent
      // failure.
      try { window.print(); } catch (_) { /* noop */ }
      done();
    }
  });
}
